/**
 * Why a local game ended.
 *
 * Extracted from LocalGame so it can be tested without a DOM. It is a pure
 * function of a Game, and it is the part that can be wrong: the wiring around it
 * is typechecked, the order of these tests is not.
 *
 * Checkmate is asked before repetition, which is defensive rather than load-
 * bearing: a repetition key is placement plus side to move, so a position either
 * is checkmate or is not, and a mated one ends the game the first time it
 * appears. It cannot also be a recurrence. Kept in this order anyway, because
 * the reader should not have to reconstruct that argument to see it is safe.
 *
 * The strings live in i18n under `reason<Slug>`; this returns only the slug, so
 * analytics and the modal cannot drift apart.
 */

import {
  Game,
  GameResult,
  isCheckmate,
  isStalemate,
  violationLevels,
  ViolationLevel,
} from "@xq/rules";

export type EndSlug =
  | "checkmate"
  | "stalemate"
  | "perpetual-check"
  | "perpetual-chase"
  | "threefold-repetition"
  | "draw";

/** The reason this game ended, or null while it is still going. */
export function endSlug(game: Game): EndSlug | null {
  if (game.outcome() === GameResult.Ongoing) return null;

  const pos = game.position;
  // Position-local endings first. A mate is a mate even if the position repeats.
  if (isCheckmate(pos)) return "checkmate";
  if (isStalemate(pos)) return "stalemate";

  const cycle = game.repetitionCycle();
  if (game.repetitionOutcome() !== null && cycle) {
    const levels = violationLevels(cycle);
    // The lattice: whichever violation the cycle topped out at is what to name.
    const worst = Math.max(levels.red, levels.black);
    if (worst === ViolationLevel.Check) return "perpetual-check";
    if (worst === ViolationLevel.Chase) return "perpetual-chase";
    return "threefold-repetition";
  }
  // Nothing else left: the sixty-move rule.
  return "draw";
}
